import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  VirtualConnectionDownstreamSheetsFilteredByTableQuery,
  VirtualConnectionDownstreamSheetsFilteredByTableQuery_virtualConnections_tablesConnection_nodes_downstreamSheetsConnection_nodes,
  VirtualConnectionDownstreamSheetsFilteredByTableQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionDownstreamSheetsFilteredByTableQuery';
import {
  VirtualConnectionDownstreamSheetsQuery,
  VirtualConnectionDownstreamSheetsQuery_virtualConnections_downstreamSheetsConnection_nodes,
  VirtualConnectionDownstreamSheetsQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionDownstreamSheetsQuery';
import { SheetSortOrder } from '../../../gen-src/queries/globalTypes';
import { VirtualConnectionDownstreamSheetsFilteredByTableQueryGql } from '../../queries/VirtualConnectionDownstreamSheetsFilteredByTableQueryGql';
import { VirtualConnectionDownstreamSheetsQueryGql } from '../../queries/VirtualConnectionDownstreamSheetsQueryGql';
import { getRowDataForSheet, SheetsDisplayGrid, SheetsDisplayGridRowData } from '../sharedDisplayGrid/SheetsDisplayGrid';
import { SheetsValidSortFields } from '../sorting/sortProviders/SheetsGridSortProvider';


type QueryVariables = VirtualConnectionDownstreamSheetsQueryVariables;
type QueryResults = VirtualConnectionDownstreamSheetsQuery;
type QueryResultListNode = VirtualConnectionDownstreamSheetsQuery_virtualConnections_downstreamSheetsConnection_nodes;
type GridRowData = SheetsDisplayGridRowData;

type QueryVariablesWithFilter = VirtualConnectionDownstreamSheetsFilteredByTableQueryVariables;
type QueryResultsWithFilter = VirtualConnectionDownstreamSheetsFilteredByTableQuery;
type QueryResultListNodeWithFilter =
  VirtualConnectionDownstreamSheetsFilteredByTableQuery_virtualConnections_tablesConnection_nodes_downstreamSheetsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.virtualConnections && resultData.virtualConnections[0] &&
    resultData.virtualConnections[0].downstreamSheetsConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const virtualConnection = resultData && resultData.virtualConnections && resultData.virtualConnections[0];
  const virtualConnectionTables = virtualConnection && virtualConnection.tablesConnection && virtualConnection.tablesConnection.nodes;
  const sheet = virtualConnectionTables && virtualConnectionTables[0];
  return sheet ? sheet.downstreamSheetsConnection : null;
}

export type VirtualConnectionDownstreamSheetsQueryGridProps = GridSortProps<SheetsValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredTableId: string | null;
  readonly rowCount: number | null;
};

export function VirtualConnectionDownstreamSheetsQueryGrid(props: VirtualConnectionDownstreamSheetsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamSheetsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredSheetsLineageGridMessage();

  return props.filteredTableId ? (
    // Query with Selected Sheet as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionDownstreamSheetsFilteredByTableQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        selectedTableId: props.filteredTableId,
        orderBy: props.orderBy as SheetSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForSheet}
    >
      {({ rowStore }) =>
        <SheetsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={VirtualConnectionDownstreamSheetsQueryGql}
      partialQueryVariables={{
        vizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as SheetSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForSheet}
    >
      {({ rowStore }) =>
        <SheetsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
